import { isCapacitorRuntime } from "../config/vars";

const KEY_ENABLED = "WR_CLIENT_WEATHER_ENABLED";
const KEY_CITY = "WR_CLIENT_WEATHER_CITY";
const KEY_SHOW_WIND = "WR_CLIENT_WEATHER_SHOW_WIND";
const KEY_SHOW_PRESSURE = "WR_CLIENT_WEATHER_SHOW_PRESSURE";
const KEY_SHOW_HUMIDITY = "WR_CLIENT_WEATHER_SHOW_HUMIDITY";
const KEY_CACHE = "WR_CLIENT_WEATHER_CACHE";

const CACHE_TTL_MS = 30 * 60 * 1000;

function cleanCity(value) {
    const city = String(value || "").trim();
    return city;
}

function safeJsonParse(value) {
    if (!value) return null;
    try {
        return JSON.parse(value);
    } catch (_) {
        return null;
    }
}

function formatTempC(tempC) {
    if (typeof tempC !== "number" || Number.isNaN(tempC)) return null;
    const rounded = Math.round(tempC);
    const sign = rounded > 0 ? "+" : "";
    return `${sign}${rounded}°C`;
}

export function getWeatherEnabled() {
    try {
        const v = localStorage.getItem(KEY_ENABLED);
        if (v === null) return true;
        return v === "1";
    } catch (_) {
        return true;
    }
}

function _getBoolWithDefault(key, defaultValue) {
    try {
        const v = localStorage.getItem(key);
        if (v === null) return !!defaultValue;
        return v === "1";
    } catch (_) {
        return !!defaultValue;
    }
}

function _setBool(key, enabled) {
    try {
        localStorage.setItem(key, enabled ? "1" : "0");
    } catch (_) {
        // ignore
    }
    try {
        window.dispatchEvent(new Event("wrWeatherChanged"));
    } catch (_) {
        // ignore
    }
}

export function getWeatherShowWind() {
    return _getBoolWithDefault(KEY_SHOW_WIND, true);
}

export function getWeatherShowPressure() {
    return _getBoolWithDefault(KEY_SHOW_PRESSURE, true);
}

export function getWeatherShowHumidity() {
    return _getBoolWithDefault(KEY_SHOW_HUMIDITY, true);
}

export function setWeatherShowWind(enabled) {
    _setBool(KEY_SHOW_WIND, enabled);
}

export function setWeatherShowPressure(enabled) {
    _setBool(KEY_SHOW_PRESSURE, enabled);
}

export function setWeatherShowHumidity(enabled) {
    _setBool(KEY_SHOW_HUMIDITY, enabled);
}

export function getWeatherCity() {
    try {
        return cleanCity(localStorage.getItem(KEY_CITY)) || "Львів";
    } catch (_) {
        return "Львів";
    }
}

export function setWeatherEnabled(enabled) {
    _setBool(KEY_ENABLED, enabled);
}

export function setWeatherCity(city) {
    const cleaned = cleanCity(city);
    try {
        localStorage.setItem(KEY_CITY, cleaned);
    } catch (_) {
        // ignore
    }
    try {
        // city change invalidates cache
        localStorage.removeItem(KEY_CACHE);
    } catch (_) {
        // ignore
    }
    try {
        window.dispatchEvent(new Event("wrWeatherChanged"));
    } catch (_) {
        // ignore
    }
}

async function geocodeCity(city) {
    const name = cleanCity(city);
    if (!name) return null;

    const url = new URL("https://geocoding-api.open-meteo.com/v1/search");
    url.searchParams.set("name", name);
    url.searchParams.set("count", "1");
    url.searchParams.set("language", "uk");
    url.searchParams.set("format", "json");

    const res = await fetch(url.toString());
    if (!res.ok) return null;
    const data = await res.json();

    const first = Array.isArray(data?.results) ? data.results[0] : null;
    if (!first) return null;

    const lat = Number(first.latitude);
    const lon = Number(first.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;

    return { lat, lon, name: first.name || name };
}

async function fetchCurrentTempC(lat, lon) {
    const url = new URL("https://api.open-meteo.com/v1/forecast");
    url.searchParams.set("latitude", String(lat));
    url.searchParams.set("longitude", String(lon));
    url.searchParams.set(
        "current",
        "temperature_2m,relative_humidity_2m,pressure_msl,wind_speed_10m,wind_direction_10m"
    );
    url.searchParams.set("timezone", "auto");
    url.searchParams.set("wind_speed_unit", "ms");

    const res = await fetch(url.toString());
    if (!res.ok) return null;
    const data = await res.json();

    const tempC = Number(data?.current?.temperature_2m);
    if (!Number.isFinite(tempC)) return null;

    const humidity = Number(data?.current?.relative_humidity_2m);
    const pressure = Number(data?.current?.pressure_msl);
    const windSpeed = Number(data?.current?.wind_speed_10m);
    const windDir = Number(data?.current?.wind_direction_10m);

    return {
        tempC,
        humidity: Number.isFinite(humidity) ? humidity : null,
        pressure: Number.isFinite(pressure) ? pressure : null,
        windSpeed: Number.isFinite(windSpeed) ? windSpeed : null,
        windDir: Number.isFinite(windDir) ? windDir : null,
    };
}

function windDirToUk(deg) {
    if (typeof deg !== "number" || !Number.isFinite(deg)) return null;
    const dirs = ["Пн", "ПнСх", "Сх", "ПдСх", "Пд", "ПдЗх", "Зх", "ПнЗх"];
    const idx = Math.round(((deg % 360) + 360) % 360 / 45) % 8;
    return dirs[idx] || null;
}

function buildWeatherText(w) {
    if (!w || typeof w !== "object") return null;
    const temp = formatTempC(w.tempC);
    if (!temp) return null;

    const parts = [temp];

    if (getWeatherShowWind() && typeof w.windSpeed === "number" && Number.isFinite(w.windSpeed)) {
        const speed = Math.round(w.windSpeed);
        const dir = windDirToUk(w.windDir);
        parts.push(`вітер ${speed} м/с${dir ? " " + dir : ""}`);
    }

    if (getWeatherShowPressure() && typeof w.pressure === "number" && Number.isFinite(w.pressure)) {
        parts.push(`${Math.round(w.pressure)} гПа`);
    }

    if (getWeatherShowHumidity() && typeof w.humidity === "number" && Number.isFinite(w.humidity)) {
        parts.push(`${Math.round(w.humidity)}%`);
    }

    return parts.join(", ");
}

export async function loadWeatherTextForRibbon() {
    if (!isCapacitorRuntime()) return null;

    if (!getWeatherEnabled()) return null;
    const city = getWeatherCity();
    if (!city) return null;

    // cache
    try {
        const cached = safeJsonParse(localStorage.getItem(KEY_CACHE));
        if (cached && cached.city === city && typeof cached.fetchedAt === "number") {
            const age = Date.now() - cached.fetchedAt;
            if (age >= 0 && age <= CACHE_TTL_MS) {
                if (cached.weather && typeof cached.weather === "object") {
                    const text = buildWeatherText(cached.weather);
                    if (text) return text;
                }

                // backwards compatibility for old cache entries
                if (typeof cached.tempC === "number") {
                    return formatTempC(cached.tempC);
                }
            }
        }
    } catch (_) {
        // ignore
    }

    try {
        const geo = await geocodeCity(city);
        if (!geo) return null;

        const weather = await fetchCurrentTempC(geo.lat, geo.lon);
        if (!weather || typeof weather !== "object") return null;
        if (typeof weather.tempC !== "number" || Number.isNaN(weather.tempC)) return null;

        try {
            localStorage.setItem(
                KEY_CACHE,
                JSON.stringify({ city, fetchedAt: Date.now(), weather })
            );
        } catch (_) {
            // ignore
        }

        return buildWeatherText(weather);
    } catch (_) {
        return null;
    }
}
